import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check, Copy, Printer, SearchX, ShieldCheck, ShieldX } from 'lucide-react';
import { CertificateApiService } from '@/services/course-content.service';
import { ApiCertificateVerificationResponse } from '@/components/admin/Courses/CourseBuilder/types/courseContentApi.types';
import { GuestShell } from '@/components/guest/GuestShell';
import { useMultilang } from '@/hooks/useMultilang';
import s from './CertificateVerificationPage.module.css';

import { tr, appLocale } from '@/i18n';

type Outcome = 'valid' | 'revoked' | 'not_found';

const outcomeOf = (r: ApiCertificateVerificationResponse | null): Outcome => {
  if (!r) return 'not_found';
  if (r.status === 'valid' || r.status === 'revoked' || r.status === 'not_found') return r.status;
  // Older API: only is_valid + message.
  if (r.is_valid) return 'valid';
  return r.message === 'This certificate has been revoked.' ? 'revoked' : 'not_found';
};

/** Public certificate verification — what the QR code on every certificate opens. Anyone (no sign-in)
 *  sees whether the certificate is genuine, who it was awarded to, for what, when and by whom.
 *  The id comes from the route (/verify/:certId) or the query string (?cert_id=). */
export const CertificateVerificationPage: React.FC = () => {
  const { ml } = useMultilang();
  const navigate = useNavigate();
  const { certId: certIdParam } = useParams<{ certId: string }>();
  const [searchParams] = useSearchParams();
  const certId = certIdParam || searchParams.get('cert_id') || '';

  const [loading, setLoading] = useState(!!certId);
  const [result, setResult] = useState<ApiCertificateVerificationResponse | null>(null);
  const [manual, setManual] = useState('');
  const [copied, setCopied] = useState(false);
  const [checkedAt] = useState(() => new Date());

  useEffect(() => {
    if (!certId) return;
    let alive = true;
    setLoading(true);
    CertificateApiService.verifyCertificate(certId)
      .then((r) => alive && setResult(r))
      .catch(() => alive && setResult(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [certId]);

  const lookup = (e: React.FormEvent) => {
    e.preventDefault();
    const id = manual.trim();
    if (id) navigate(`/verify/${encodeURIComponent(id)}`);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — nothing to do */
    }
  };

  const outcome = outcomeOf(result);
  const name = result?.student_name || '';
  const what = ml(result?.course_name || '');
  const issuer = result?.issuer || '';
  const longDate = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString(appLocale(), { day: 'numeric', month: 'long', year: 'numeric' }) : '';

  const lookupForm = (
    <form className={s.lookup} onSubmit={lookup}>
      <input
        className={s.input}
        value={manual}
        onChange={(e) => setManual(e.target.value)}
        placeholder={tr("ui.certificate_id", "Certificate ID")}
        aria-label={tr("ui.certificate_id", "Certificate ID")}
      />
      <button type="submit" className={s.btnPrimary}>{tr("ui.verify", "Verify")}</button>
    </form>
  );

  let body: React.ReactNode;
  if (loading) {
    body = <div className={s.loading}>{tr("ui.verifying", "Verifying…")}</div>;
  } else if (!certId) {
    body = (
      <div className={`${s.hero} ${s.heroMissing}`}>
        <span className={`${s.seal} ${s.sealMissing}`}><ShieldCheck size={38} /></span>
        <h1 className={s.title}>{tr("ui.certificate_verification", "Certificate Verification")}</h1>
        <p className={s.lead}>{tr("ui.enter_a_certificate_id_to_verify_it", "Enter a certificate ID to verify it.")}</p>
        {lookupForm}
      </div>
    );
  } else if (outcome === 'not_found') {
    body = (
      <div className={`${s.hero} ${s.heroMissing}`}>
        <span className={`${s.seal} ${s.sealMissing}`}><SearchX size={36} /></span>
        <p className={s.kicker}>{tr("ui.certificate_verification", "Certificate Verification")}</p>
        <h1 className={s.title}>{tr("cert.verify.title_not_found", "Certificate not found")}</h1>
        <p className={s.lead}>
          {tr("cert.verify.not_found_lead", "No certificate has this ID. Check the link or the ID printed on the certificate, and try again.")}
        </p>
        {lookupForm}
      </div>
    );
  } else {
    const valid = outcome === 'valid';
    body = (
      <>
        <div className={`${s.hero} ${valid ? s.heroValid : s.heroRevoked}`}>
          <span className={`${s.seal} ${valid ? s.sealValid : s.sealRevoked}`}>
            {valid ? <ShieldCheck size={38} /> : <ShieldX size={38} />}
          </span>
          <p className={s.kicker}>{tr("ui.certificate_verification", "Certificate Verification")}</p>
          <h1 className={s.title}>
            {valid ? tr("cert.verify.title_valid", "Verified certificate") : tr("cert.verify.title_revoked", "This certificate has been revoked")}
          </h1>
          <p className={s.lead}>
            {valid
              ? issuer
                ? tr("cert.verify.lead_valid_by", "This is a genuine certificate issued by {v0} to {v1}.", { v0: issuer, v1: name })
                : tr("cert.verify.lead_valid", "This is a genuine certificate issued to {v0}.", { v0: name })
              : tr("cert.verify.lead_revoked", "It was issued, but the issuer has withdrawn it. It is no longer valid.")}
          </p>
        </div>
        <dl className={s.details}>
          <div className={`${s.item} ${s.itemWide}`}>
            <dt>{tr("ui.awarded_to", "Awarded to")}</dt>
            <dd>{name || '—'}</dd>
          </div>
          <div className={`${s.item} ${s.itemWide}`}>
            <dt>{result?.kind === 'program' ? tr("ui.program", "Program") : result?.kind === 'course' ? tr("gradebook.col_course", "Course") : tr("ui.course_program", "Course / Program")}</dt>
            <dd>{what || '—'}</dd>
          </div>
          <div className={s.item}>
            <dt>{tr("ui.cert_issued_on", "Issued on")}</dt>
            <dd>{longDate(result?.issue_date) || '—'}</dd>
          </div>
          <div className={s.item}>
            <dt>{tr("cert.verify.issued_by", "Issued by")}</dt>
            <dd>{issuer || '—'}</dd>
          </div>
          <div className={s.item}>
            <dt>{tr("ui.serial_number", "Serial number")}</dt>
            <dd className={s.mono}>{result?.serial_number || '—'}</dd>
          </div>
          <div className={s.item}>
            <dt>{tr("ui.certificate_id", "Certificate ID")}</dt>
            <dd className={s.mono}>{result?.cert_id || certId}</dd>
          </div>
        </dl>
        <div className={s.foot}>
          <span className={s.checked}>
            {tr("cert.verify.checked_at", "Checked {v0}", {
              v0: checkedAt.toLocaleString(appLocale(), { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
            })}
          </span>
          <div className={s.actions}>
            <button type="button" className={s.btn} onClick={copyLink}>
              {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? tr("cert.verify.copied", "Link copied") : tr("cert.verify.copy_link", "Copy link")}
            </button>
            <button type="button" className={s.btn} onClick={() => window.print()}>
              <Printer size={16} /> {tr("cert.verify.print", "Print")}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <GuestShell>
      <main className={s.main}>
        <section className={s.card} aria-live="polite">{body}</section>
      </main>
    </GuestShell>
  );
};
